import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import {
  useGetTelephonyProviders,
  useGetTelephonyProviderBusinesses,
  useUpdateTelephonyProvider
} from 'apis/agency/telephony/hook';
import {
  DomainBusinessMapping,
  NetsapiensCsp,
  TelephonyProviderConfiguration,
  TwilioBusinessMapping
} from 'apis/agency/telephony/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import * as urls from 'routes/config';
import { DEFAULT_PROVIDER, DEFAULT_TAB } from './utils/constants';
import { buildTelephonyFormSchema, enrichTelephonyFields } from './utils/formFields';
import { MapBusinessState, SupportedProvider, SupportedTab } from './utils/types';
import ProviderSidebar from './components/ProviderSidebar';
import TelephonyTopBar from './components/TelephonyTopBar';
import TelephonyApiKeyForm from './components/TelephonyApiKeyForm';
import TwilioMappingTable from './components/TwilioMappingTable';
import NetsapiensMappingTable from './components/NetsapiensMappingTable';
import NetsapiensConfigureDrawer from './components/NetsapiensConfigureDrawer';
import AsteriskMapBusinessDrawer from './components/AsteriskMapBusinessDrawer';
import Pagination, { usePagination } from 'components/Common/Pagination';
import AsteriskMappingTable from './components/AsteriskMappingTable';
import { mergeNetsapiensCsps, normalizeCsps } from './utils/netsapiensCsp';
import TelephonyDetailSkeleton from './components/TelephonyDetailSkeleton';

const NETSAPIENS_ROUTE_SEGMENT = 'netsapiens';

type TelephonyFormValues = {
  active: boolean;
  friendly_name: string;
  configuration: TelephonyProviderConfiguration;
};

const normalizeProvider = (provider?: string): SupportedProvider => {
  if (!provider) return DEFAULT_PROVIDER;
  const lower = provider.toLowerCase();
  if (lower === 'netsapiens' || lower === NETSAPIENS_ROUTE_SEGMENT) return 'netsapiens';
  if (lower === '3cx') return '3cx';
  if (lower === 'asterisk') return 'asterisk';
  if (lower === 'twilio') return 'twilio';
  if (lower === 'bandwidth') return 'bandwidth';
  return DEFAULT_PROVIDER;
};

const normalizeTab = (tab?: string): SupportedTab =>
  (tab || '').toLowerCase() === 'mapping' ? 'mapping' : DEFAULT_TAB;

const providerToRouteSegment = (provider: SupportedProvider) =>
  provider === 'netsapiens' ? NETSAPIENS_ROUTE_SEGMENT : provider;

const toTelephonyPath = (provider: SupportedProvider, tab: SupportedTab) =>
  `/agency/${urls.agency.telephony}/${providerToRouteSegment(provider)}/${tab}`;

export default function Telephony() {
  const navigate = useNavigate();
  const { provider: providerParam, tab: tabParam } = useParams<{ provider?: string; tab?: string }>();

  const provider = normalizeProvider(providerParam);
  const tab = normalizeTab(tabParam);
  const canonicalProviderSegment = providerToRouteSegment(provider);

  const [searchText, setSearchText] = useState<string | null>(null);
  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [mapBusinessState, setMapBusinessState] = useState<MapBusinessState | null>(null);

  const { data: providersData, isLoading: providersLoading } = useGetTelephonyProviders();
  const providers = useMemo(() => {
    const list = providersData?.data || [];
    const result: { label: string; value: SupportedProvider }[] = [];

    list.forEach((p) => {
      if (p.code === 'twilio') {
        result.push({ label: p.name, value: 'twilio' });
      } else if (p.code === 'netsapiens') {
        result.push({ label: 'Netsapiens', value: 'netsapiens' });
      } else if (p.code === 'asterisk') {
        result.push({ label: 'Asterisk', value: 'asterisk' });
      } else if (p.code === 'bandwidth') {
        result.push({ label: p.name, value: 'bandwidth' });
      }
    });

    return result;
  }, [providersData]);

  const activeProvider = useMemo(
    () => (providersData?.data || []).find((p) => p.code === provider),
    [providersData, provider]
  );

  const providerFields = useMemo(
    () => enrichTelephonyFields(provider, activeProvider?.fields ?? []),
    [activeProvider?.fields, provider]
  );

  const telephonyResolver = useMemo(
    () =>
      providerFields.length
        ? (zodResolver(buildTelephonyFormSchema(providerFields)) as Resolver<TelephonyFormValues>)
        : undefined,
    [providerFields]
  );

  const filter = useMemo(() => {
    const term = (searchText || '').trim();
    return term ? { name_cont: term } : undefined;
  }, [searchText]);

  const listParams = useMemo(
    () => ({
      page: currentPage,
      per_page: perPage,
      ...(filter ? { filter } : {})
    }),
    [currentPage, perPage, filter]
  );

  const { data: businessesData, isFetching: businessesLoading } = useGetTelephonyProviderBusinesses(
    activeProvider?.id,
    listParams,
    {
      enabled: Boolean(activeProvider?.id)
    }
  );

  useEffect(() => {
    resetPage();
  }, [searchText, activeProvider?.id, resetPage]);

  useEffect(() => {
    if (!providerParam || !tabParam) return;
    const currentProviderSegment = providerParam.toLowerCase();
    const currentTab = tabParam.toLowerCase();

    if (currentProviderSegment !== canonicalProviderSegment || currentTab !== tab) {
      navigate(toTelephonyPath(provider, tab), { replace: true });
    }
  }, [providerParam, tabParam, canonicalProviderSegment, tab, provider, navigate]);

  const { mutateAsync: updateProvider, isPending: isUpdatingProvider } = useUpdateTelephonyProvider(activeProvider?.id);

  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const { handleSubmit, control, reset, getValues } = useForm<TelephonyFormValues>({
    resolver: telephonyResolver,
    defaultValues: {
      active: false,
      friendly_name: '',
      configuration: { csps: [] }
    }
  });

  const serverNetsapiensCsps = activeProvider?.configuration?.csps;

  const netsapiensInitialCsps = useMemo(() => normalizeCsps(serverNetsapiensCsps), [serverNetsapiensCsps]);

  const resetTelephonyForm = useCallback(() => {
    if (!activeProvider) return;

    reset({
      active: activeProvider.active ?? false,
      friendly_name: activeProvider.friendly_name || '',
      configuration: {
        ...(activeProvider.configuration || {}),
        csps: netsapiensInitialCsps
      }
    });
  }, [activeProvider, netsapiensInitialCsps, reset]);

  useEffect(() => {
    resetTelephonyForm();
  }, [activeProvider?.id, resetTelephonyForm]);

  const handleLogoUpload = async (file: File) => {
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('telephony_provider[logo]', file);
      await updateProvider(formData);
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const netsapiensMapBusinessForm = useForm({
    defaultValues: { businessName: '', cspName: '', syncSchedule: 'hourly' }
  });

  const asteriskMapBusinessForm = useForm({
    defaultValues: { businessName: '', asteriskAccount: '', syncSchedule: '' }
  });

  useEffect(() => {
    netsapiensMapBusinessForm.reset({
      businessName: mapBusinessState?.businessName || '',
      cspName: mapBusinessState?.cspName || '',
      syncSchedule: 'hourly'
    });
    asteriskMapBusinessForm.reset({
      businessName: mapBusinessState?.businessName || '',
      asteriskAccount: mapBusinessState?.currentDomain || '',
      syncSchedule: ''
    });
  }, [mapBusinessState, netsapiensMapBusinessForm, asteriskMapBusinessForm]);

  const persistNetsapiensCsps = async (nextCsps: NetsapiensCsp[]) => {
    if (!activeProvider) return;

    const mergedCsps = mergeNetsapiensCsps(nextCsps, normalizeCsps(activeProvider.configuration?.csps));
    const formValues = getValues();

    await updateProvider({
      active: formValues.active,
      friendly_name: formValues.friendly_name,
      configuration: {
        ...(activeProvider.configuration || {}),
        csps: mergedCsps
      }
    });
  };

  const onSubmit = async (data: TelephonyFormValues) => {
    const configuration: TelephonyProviderConfiguration = {
      ...(activeProvider?.configuration || {}),
      ...data.configuration,
      csps: netsapiensInitialCsps
    };

    await updateProvider({
      active: data.active,
      friendly_name: data.friendly_name,
      configuration
    });
  };

  const onAsteriskMapBusinessSave = async () => {
    setMapBusinessState(null);
  };

  const twilioRows = useMemo(() => (businessesData?.data || []) as TwilioBusinessMapping[], [businessesData]);

  const domainRows = useMemo(() => (businessesData?.data || []) as DomainBusinessMapping[], [businessesData]);

  const businessCount = businessesData?.meta?.total_count ?? businessesData?.data?.length ?? 0;

  if (!providerParam || !tabParam) {
    return <Navigate to={toTelephonyPath(provider, tab)} replace />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2.5}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
      >
        <ProviderSidebar providers={providers} tab={tab} isLoading={providersLoading && providers.length === 0} />

        <Box flex={1}>
          {providersLoading ? (
            <TelephonyDetailSkeleton />
          ) : (
            <>
              <TelephonyTopBar
                provider={provider}
                tab={tab}
                businessCount={businessCount}
                loadingBusinessCount={businessesLoading}
                articleId={activeProvider?.article_id}
                description={activeProvider?.description}
                onTabChange={(nextTab) => navigate(toTelephonyPath(provider, nextTab))}
              />

              {tab === 'api-key' && (
                <TelephonyApiKeyForm
                  control={control}
                  fields={providerFields}
                  onReset={resetTelephonyForm}
                  onSubmit={handleSubmit(onSubmit)}
                  logoUrl={activeProvider?.logo_url}
                  onLogoUpload={handleLogoUpload}
                  isUploadingLogo={isUploadingLogo}
                  isPending={isUpdatingProvider}
                  provider={provider}
                  netsapiensInitialCsps={netsapiensInitialCsps}
                  onPersistNetsapiensCsps={persistNetsapiensCsps}
                />
              )}

              {tab === 'mapping' && (
                <>
                  {provider === 'twilio' || provider === 'bandwidth' ? (
                    <TwilioMappingTable
                      rows={twilioRows}
                      searchText={searchText}
                      setSearchText={setSearchText}
                      isLoading={businessesLoading}
                    />
                  ) : provider === 'asterisk' ? (
                    <AsteriskMappingTable
                      rows={domainRows}
                      searchText={searchText}
                      setSearchText={setSearchText}
                      isLoading={businessesLoading}
                      onConfigure={(business) =>
                        setMapBusinessState({
                          businessId: business.id,
                          businessName: business.name,
                          currentDomain: business.external_reference_name
                        })
                      }
                    />
                  ) : (
                    <NetsapiensMappingTable
                      rows={domainRows}
                      searchText={searchText}
                      setSearchText={setSearchText}
                      isLoading={businessesLoading}
                      onConfigure={(business) =>
                        setMapBusinessState({
                          businessId: business.id,
                          businessName: business.name,
                          cspName: business.csp_name
                        })
                      }
                    />
                  )}
                  <Pagination
                    totalPages={businessesData?.meta?.total_pages}
                    totalCount={businessesData?.meta?.total_count ?? 0}
                    currentPage={currentPage}
                    perPage={perPage}
                    onChange={setPage}
                    onPerPageChange={setPerPage}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {provider === 'asterisk' ? (
        <AsteriskMapBusinessDrawer
          openState={mapBusinessState}
          onClose={() => setMapBusinessState(null)}
          onSave={onAsteriskMapBusinessSave}
          form={asteriskMapBusinessForm}
        />
      ) : provider === 'netsapiens' ? (
        <NetsapiensConfigureDrawer
          openState={mapBusinessState}
          onClose={() => setMapBusinessState(null)}
          form={netsapiensMapBusinessForm}
          providerId={activeProvider?.id}
        />
      ) : null}
    </Box>
  );
}
